import { useEffect, useState } from "react";
import { MapPin, Mail, Phone, Send, Sparkles, ShieldCheck, MessageCircle } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import api, { formatApiErrorDetail } from "@/lib/api";

const FAQ_FALLBACK = {
  eyebrow: "FAQs",
  heading_lead: "Got questions?",
  heading_accent: "We've got you.",
  items: [
    { q: "What time does Sunday Service begin?", a: "We have two Sunday services. The first service starts at 9:00 AM, followed by the second service at 11:15 AM." },
    { q: "Is everyone welcome to attend?", a: "Yes — everyone is welcome to worship, fellowship and grow in faith with us." },
  ],
};

const CONTACT_FALLBACK = {
  address_line1: "Room 105–108, Speedex Building",
  address_line2: "Abubaker Al Siddique Metro Station, Exit 1",
  address_line3: "Dubai, United Arab Emirates",
  email: "ugyouthchurch@gmail.com",
  phone: "",
  whatsapp: "",
  map_query: "Abubaker Al Siddique Metro Station, Dubai, UAE",
  map_link: "",
};

export default function Contact() {
  const [tab, setTab] = useState("contact");
  const [faq, setFaq] = useState(FAQ_FALLBACK);
  const [info, setInfo] = useState(CONTACT_FALLBACK);

  useEffect(() => {
    api.get("/content/contact_faqs")
      .then((r) => {
        const d = r.data || {};
        if (Array.isArray(d.items) && d.items.length) setFaq({ ...FAQ_FALLBACK, ...d });
      })
      .catch(() => {});
    api.get("/content/contact_info")
      .then((r) => {
        const d = r.data || {};
        if (d && Object.keys(d).length) setInfo({ ...CONTACT_FALLBACK, ...d });
      })
      .catch(() => {});
  }, []);

  const waDigits = (info.whatsapp || "").replace(/[^\d]/g, "");

  return (
    <div data-testid="contact-page" className="bg-[#050505] text-white">
      <section className="py-32 md:py-40 px-6 md:px-12 border-b border-white/10">
        <div className="max-w-7xl mx-auto">
          <p className="font-mono-accent text-xs tracking-[0.3em] uppercase text-[#ff5c00] mb-4">Reach Out</p>
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl uppercase font-bold tracking-tighter leading-[0.9]">
            Let's <span className="text-[#ff5c00]">connect</span>.
          </h1>
          <p className="text-zinc-300 text-lg md:text-xl max-w-3xl mt-8">
            Whether you want to plan a visit, send a prayer request, or just
            say hi — we're here. Pick a tab and let's go.
          </p>
        </div>
      </section>

      <section className="py-16 px-6 md:px-12">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10">
          <aside className="lg:col-span-5 space-y-6">
            <div className="bg-[#0f0f14] border border-white/10 rounded-sm p-7 space-y-5">
              <div>
                <p className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2">Location</p>
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#ff5c00] mt-0.5" />
                  <p data-testid="contact-address" className="text-white">
                    {info.address_line1}{info.address_line2 && <><br />{info.address_line2}</>}{info.address_line3 && <><br />{info.address_line3}</>}
                    {info.map_link && (
                      <><br /><a href={info.map_link} target="_blank" rel="noreferrer" data-testid="contact-maps-link" className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-[#ff5c00] hover:underline mt-2 inline-block">Open in Google Maps →</a></>
                    )}
                  </p>
                </div>
              </div>
              {info.email && (
                <div>
                  <p className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2">Email</p>
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-[#ff5c00]" />
                    <a href={`mailto:${info.email}`} data-testid="contact-email-link" className="text-white hover:text-[#ff5c00]">{info.email}</a>
                  </div>
                </div>
              )}
              {(info.phone || info.whatsapp) && (
                <div>
                  <p className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2">Phone / WhatsApp</p>
                  <div className="space-y-2">
                    {info.phone && (
                      <div className="flex items-center gap-3">
                        <Phone className="w-5 h-5 text-[#ff5c00]" />
                        <a href={`tel:${info.phone.replace(/\s+/g, "")}`} data-testid="contact-phone-link" className="text-white hover:text-[#ff5c00]">{info.phone}</a>
                      </div>
                    )}
                    {info.whatsapp && waDigits && (
                      <div className="flex items-center gap-3">
                        <MessageCircle className="w-5 h-5 text-[#ff5c00]" />
                        <a href={`https://wa.me/${waDigits}`} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-link" className="text-white hover:text-[#ff5c00]">{info.whatsapp}</a>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div>
                <p className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2">Service Times</p>
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-[#ff5c00] mt-0.5" />
                  <div className="text-white text-sm space-y-1">
                    <p>Sunday · 9:00 AM &amp; 11:15 AM</p>
                    <p>Sunday School · 10:30 – 11:15 AM</p>
                    <p>Wednesday · Word of Life — 7:00 PM</p>
                    <p className="text-zinc-400 text-xs pl-3">(1st Wed of every month = Communion)</p>
                    <p>Saturday · Evangelism — 4:00 PM</p>
                    <p>Daily · Open Heavens — 5:45 AM (Instagram)</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="aspect-[4/3] rounded-sm overflow-hidden border border-white/10">
              <iframe
                title="UGYC Dubai location map"
                data-testid="dubai-map"
                src={`https://www.google.com/maps?q=${encodeURIComponent(info.map_query || "Abubaker Al Siddique Metro Station, Dubai, UAE")}&output=embed`}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </aside>

          <div className="lg:col-span-7 glass rounded-sm p-7 md:p-10">
            <div className="flex gap-3 mb-8">
              <button
                onClick={() => setTab("contact")}
                data-testid="tab-contact"
                className={`font-mono-accent text-xs tracking-[0.2em] uppercase px-4 py-2 rounded-sm border transition-colors ${tab === "contact" ? "border-[#ff5c00] text-white bg-[#ff5c00]/10" : "border-white/10 text-zinc-400 hover:text-white"}`}
              >
                Contact
              </button>
              <button
                onClick={() => setTab("prayer")}
                data-testid="tab-prayer"
                className={`font-mono-accent text-xs tracking-[0.2em] uppercase px-4 py-2 rounded-sm border transition-colors ${tab === "prayer" ? "border-[#ff5c00] text-white bg-[#ff5c00]/10" : "border-white/10 text-zinc-400 hover:text-white"}`}
              >
                Prayer Request
              </button>
            </div>

            {tab === "contact" ? <ContactForm /> : <PrayerForm />}
          </div>
        </div>
      </section>

      <section data-testid="faq-section" className="py-24 px-6 md:px-12 bg-[#080808] border-t border-white/5">
        <div className="max-w-4xl mx-auto">
          <p className="font-mono-accent text-xs tracking-[0.3em] uppercase text-[#ff5c00] mb-3">{faq.eyebrow || "FAQs"}</p>
          <h2 className="font-display text-3xl md:text-5xl uppercase tracking-tight mb-10">
            {faq.heading_lead || "Got questions?"} <span className="text-[#ff5c00]">{faq.heading_accent || "We've got you."}</span>
          </h2>
          <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
            {(faq.items || []).map((f, i) => (
              <AccordionItem
                key={`${i}-${f.q}`}
                value={`faq-${i}`}
                data-testid={`faq-item-${i}`}
                className="bg-[#0f0f14] border border-white/10 rounded-sm px-6 hover:border-[#ff5c00]/40 transition-colors"
              >
                <AccordionTrigger className="font-display text-base md:text-lg uppercase tracking-tight text-left hover:no-underline py-5">
                  <span className="flex items-center gap-4">
                    <span className="font-mono-accent text-xs text-[#ff5c00] w-8">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    {f.q}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-zinc-300 leading-relaxed pb-5 pl-12">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
}

function Field({ label, ...rest }) {
  return (
    <label className="block">
      <span className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2 block">{label}</span>
      <input
        className="w-full bg-[#0a0a0d] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#ff5c00] rounded-sm"
        {...rest}
      />
    </label>
  );
}

function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", subject: "General Inquiry", message: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      await api.post("/contact", form);
      setStatus("success");
      setForm({ name: "", email: "", subject: "General Inquiry", message: "" });
    } catch (err) {
      setStatus("error");
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not send.");
    }
  };

  return (
    <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Your Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" />
        <Field label="Email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" />
      </div>
      <Field label="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-subject" />
      <label className="block">
        <span className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2 block">Message</span>
        <textarea
          required
          rows={5}
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          data-testid="contact-message"
          className="w-full bg-[#0a0a0d] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#ff5c00] rounded-sm resize-none"
        />
      </label>
      <button
        type="submit"
        disabled={status === "loading"}
        data-testid="contact-submit"
        className="inline-flex items-center gap-2 bg-[#ff5c00] hover:bg-[#ff7a33] disabled:opacity-60 text-white font-mono-accent text-xs tracking-[0.18em] uppercase px-7 py-4 rounded-sm transition-all hover:-translate-y-0.5"
      >
        <Send className="w-4 h-4" /> {status === "loading" ? "Sending…" : "Send Message"}
      </button>
      {status === "success" && <p data-testid="contact-success" className="text-[#22c55e] text-sm flex items-center gap-2"><Sparkles className="w-4 h-4" /> Thanks! We'll get back to you shortly.</p>}
      {status === "error" && <p data-testid="contact-error" className="text-red-400 text-sm">{error}</p>}
    </form>
  );
}

function PrayerForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", request: "", is_anonymous: false });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      const payload = {
        ...form,
        email: form.email || null,
        phone: form.phone || null,
      };
      await api.post("/prayer-requests", payload);
      setStatus("success");
      setForm({ name: "", email: "", phone: "", request: "", is_anonymous: false });
    } catch (err) {
      setStatus("error");
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not send.");
    }
  };

  return (
    <form onSubmit={submit} data-testid="prayer-form" className="space-y-5">
      <p className="text-zinc-400 text-sm flex items-start gap-2">
        <ShieldCheck className="w-4 h-4 text-[#ff5c00] mt-0.5" />
        Your request goes straight to our intercessors. We treat it with care and confidentiality.
      </p>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Your Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="prayer-name" />
        <Field label="Email (optional)" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="prayer-email" />
      </div>
      <Field label="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="prayer-phone" />
      <label className="block">
        <span className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2 block">Your Prayer Request</span>
        <textarea
          required
          rows={5}
          value={form.request}
          onChange={(e) => setForm({ ...form, request: e.target.value })}
          data-testid="prayer-request"
          className="w-full bg-[#0a0a0d] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#ff5c00] rounded-sm resize-none"
        />
      </label>
      <label className="flex items-center gap-3 text-sm text-zinc-300 cursor-pointer">
        <input
          type="checkbox"
          checked={form.is_anonymous}
          onChange={(e) => setForm({ ...form, is_anonymous: e.target.checked })}
          data-testid="prayer-anonymous"
          className="w-4 h-4 accent-[#ff5c00]"
        />
        Keep this anonymous
      </label>
      <button
        type="submit"
        disabled={status === "loading"}
        data-testid="prayer-submit"
        className="inline-flex items-center gap-2 bg-[#ff5c00] hover:bg-[#ff7a33] disabled:opacity-60 text-white font-mono-accent text-xs tracking-[0.18em] uppercase px-7 py-4 rounded-sm transition-all hover:-translate-y-0.5"
      >
        <Send className="w-4 h-4" /> {status === "loading" ? "Sending…" : "Submit Request"}
      </button>
      {status === "success" && <p data-testid="prayer-success" className="text-[#22c55e] text-sm flex items-center gap-2"><Sparkles className="w-4 h-4" /> Received. We're praying with you.</p>}
      {status === "error" && <p data-testid="prayer-error" className="text-red-400 text-sm">{error}</p>}
    </form>
  );
}
